import { Stack } from 'component-library';
import { FC, HTMLAttributes, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { PlotReportFactLabel, PlotReportFactLabelNameEnum, ValueClass } from '@/api/rest/resources/types/fact';
// import { RenderConfidenceInterval } from './ConfidenceInterval'; TODO: MVP-5260 - Bring Confidence Interval back to charts
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePlotGroupFilterEndYear } from '@/hooks/usePlotGroupFilterEndYear';
import { usePlotGroupFilterStartYear } from '@/hooks/usePlotGroupFilterStartYear';
import { Logger } from '@/lib/logs/logger';
import { isNullish } from '@/utils/booleans';
import { formatUnit } from '@/utils/formatting';
import { printYear } from '@/utils/formatting/date';
import { getColorForIndicator } from '@/utils/getColorForIndicator';

import { ValueClassPill } from '../Pill/ValueClassPill';
import { Area, Chart, ComposedChart, getXAxisProps, TooltipProps } from './components/Chart';
import { CHART_ZOOM } from './components/ChartZoom';
import { ChartLegendBar } from './components/LegendBar';
import {
  ChartTooltip,
  // ChartTooltipConfidenceInterval, TODO: MVP-5260 - Bring Confidence Interval back to charts
  ChartTooltipDot,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from './components/Tooltip';
import { ChartLegendBarItem } from './components/types';
import { useYAxisDomainFromMouseWheelZoom } from './hooks/useYAxisDomainFromMouseWheelZoom';
import { NoChartData } from './NoChartData';
import { ChartProps } from './types';
import { getChartDataFromGraphFact, getXAxisDomainForHistoricGraph, sortChartData } from './utils';
import { getChartZoomedDomain } from './utils/getChartZoomedDomain';

const CANOPY_COVER_LABEL = 'global.analysis.canopyCover';
const UNIT = formatUnit(UnitEnum['%']);

const canopyCoverColors = getColorForIndicator(INDICATOR.CANOPY_COVER);

const chartColors = {
  canopyCover: canopyCoverColors,
  confidenceInterval: {
    fill: canopyCoverColors.confidenceInterval,
  },
} as const;

const factNames = {
  // TODO: MVP-4772 - change with forest fact
  permanent_soil_carbon_total: 'canopyCover',
};

export const CanopyCoverHistoricalChart: FC<HTMLAttributes<HTMLDivElement> & ChartProps> = ({
  data,
  showTooltip,
  zoom,
  setZoom,
  ...delegated
}) => {
  const { t } = useTranslation();
  const filtersStartYear = usePlotGroupFilterStartYear();
  const limitMaxYear = usePlotGroupFilterEndYear();

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const defaultDomain: [number, number] = [0, 100];
  const yAxisDomainFromMouseWheel = useYAxisDomainFromMouseWheelZoom(chartContainerRef, defaultDomain, setZoom);

  const dateDataMap = getChartDataFromGraphFact(data, factNames); // TODO: MVP-4772 - check if this function needs a formatter when a real forest fact is used
  const sortedChartData = sortChartData(dateDataMap, (date) => new Date(date).getTime());

  const firstTick = sortedChartData.at(0);
  const lastTick = sortedChartData.at(-1);

  if (!firstTick || !lastTick) {
    Logger.error('Not enough data to render chart');
    return (
      <div className='relative flex h-full w-full flex-col justify-center' {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const xTicksDomain = getXAxisDomainForHistoricGraph(firstTick.label, lastTick.label, filtersStartYear, limitMaxYear);
  const xAxisProps = {
    ...getXAxisProps(xTicksDomain),
    tickFormatter: printYear,
  };

  const yAxisDomainFromButtons = getChartZoomedDomain({
    data,
    zoom,
    defaultDomain,
  });

  const yAxisDomain = zoom === CHART_ZOOM.MOUSE_WHEEL ? yAxisDomainFromMouseWheel : yAxisDomainFromButtons;

  return (
    <>
      <Chart.Container
        {...delegated}
        data={sortedChartData}
        config={{
          canopyCover: {
            label: t(CANOPY_COVER_LABEL),
            color: chartColors.canopyCover.stroke,
          },
        }}
      >
        <ComposedChart data={sortedChartData}>
          <defs>
            <linearGradient id='canopyCover' x1='0' y1='-2.6' x2='0' y2='1'>
              <stop offset='0%' stopColor={chartColors.canopyCover.fill} stopOpacity={1} />
              <stop offset='100%' stopColor={chartColors.canopyCover.fill} stopOpacity={0} />
            </linearGradient>
          </defs>
          {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
          {/* {RenderConfidenceInterval({
            dataKey: 'canopyCover.confidence_interval',
            type: 'monotone',
            fill: chartColors.confidenceInterval.fill,
            id: 'canopy-cover-confidence-interval',
          })} */}
          <Area
            type='monotone'
            stroke='var(--color-canopyCover)'
            strokeWidth={2}
            fill='url(#canopyCover)'
            fillOpacity={1}
            dataKey='canopyCover.value'
            id='canopy-cover-area'
            connectNulls
            activeDot={ChartTooltipDot}
          />
          <Chart.XAxis {...xAxisProps} className='canopy-cover-x-axis' />
          <Chart.YAxis domain={yAxisDomain} className='canopy-cover-y-axis' allowDataOverflow={true}>
            <Chart.Label>{UNIT}</Chart.Label>
          </Chart.YAxis>
          {showTooltip && <Chart.Tooltip content={(props) => <CustomTooltip {...props} />} />}
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar />
    </>
  );
};

const CustomLegendBar = () => {
  const { t } = useTranslation();
  // TODO: MVP-5260 - Bring Confidence Interval back to charts
  // const confidenceIntervalLegendItem = useConfidenceIntervalLegendItem({
  //   confidenceIntervalColor: chartColors.confidenceInterval.fill,
  // });

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t(CANOPY_COVER_LABEL),
      color: chartColors.canopyCover.fill,
    },
    // ...confidenceIntervalLegendItem,
  ];

  return <ChartLegendBar items={legendBarItems} className='sm:ml-[28px]' data-testid='canopy-cover-chart-legend' />;
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();

  const canopyCoverValue = payload?.find((data) => data.dataKey === 'canopyCover.value');
  const canopyCoverDisplay = !isNullish(canopyCoverValue?.value)
    ? `${getDisplayNumber(canopyCoverValue?.value as string, window.navigator.language)} ${UNIT}`
    : t('global.analysis.noData');

  const dataPointHasLabel = canopyCoverValue && canopyCoverValue?.payload?.canopyCover?.labels?.length > 0;
  const dataPointValueClass = dataPointHasLabel
    ? canopyCoverValue.payload.canopyCover.labels.find(
        (dataLabel: PlotReportFactLabel) => dataLabel.name === PlotReportFactLabelNameEnum.value_class,
      )
    : undefined;
  const dataLabel = dataPointValueClass?.value as ValueClass;

  if (!active) return null;

  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printYear(label)}</ChartTooltipTextSecondary>
        {dataLabel && <ValueClassPill title={dataLabel} />}
      </Stack>
      <Stack spacing={8} direction='row'>
        <ChartTooltipTextPrimary>
          {t('shared.ncaDetail.details.forest.labels.averageCanopyCover')}
        </ChartTooltipTextPrimary>
        <ChartTooltipTextPrimary>{canopyCoverDisplay}</ChartTooltipTextPrimary>
      </Stack>
      {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
      {/* <ChartTooltipConfidenceInterval ciPct={canopyCoverValue?.payload?.canopyCover?.confidence_interval_pct} /> */}
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipCanopyCoverHistorical };
